import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Collapse,
  Form,
  Image,
  Input,
  InputNumber,
  Switch,
  Upload,
  message,
  Space,
  Popconfirm,
  Spin,
} from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getBusiness } from "../../../api/business";
import { updateBusiness } from "../../../api/payment";
import { serviceImage } from "../../../api/settings";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["link"],
    ["clean"],
  ],
};

const DEFAULT_TABS = () => [
  {
    tabId: "tab-deals",
    label: "Deals",
    visible: true,
    sortOrder: 0,
    contentKey: "deals",
    richTextHtml: "",
    galleryImageUrls: [],
    workingHoursHtml: "",
    contactIntroHtml: "",
  },
  {
    tabId: "tab-about",
    label: "About us",
    visible: true,
    sortOrder: 1,
    contentKey: "about",
    richTextHtml: "",
    galleryImageUrls: [],
    workingHoursHtml: "",
    contactIntroHtml: "",
  },
  {
    tabId: "tab-contact",
    label: "Contact",
    visible: true,
    sortOrder: 2,
    contentKey: "contact",
    richTextHtml: "",
    galleryImageUrls: [],
    workingHoursHtml: "",
    contactIntroHtml: "",
  },
  {
    tabId: "tab-photos",
    label: "Photos",
    visible: true,
    sortOrder: 3,
    contentKey: "photos",
    richTextHtml: "",
    galleryImageUrls: [],
    workingHoursHtml: "",
    contactIntroHtml: "",
  },
];

function normalizeTabs(raw) {
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((t, i) => ({
      tabId: String(t.tabId || `tab-${i}`),
      label: String(t.label || "Tab"),
      visible: t.visible !== false,
      sortOrder: typeof t.sortOrder === "number" ? t.sortOrder : i,
      contentKey: String(t.contentKey || "custom"),
      richTextHtml: typeof t.richTextHtml === "string" ? t.richTextHtml : "",
      galleryImageUrls: Array.isArray(t.galleryImageUrls) ? [...t.galleryImageUrls] : [],
      workingHoursHtml: typeof t.workingHoursHtml === "string" ? t.workingHoursHtml : "",
      contactIntroHtml: typeof t.contactIntroHtml === "string" ? t.contactIntroHtml : "",
    }));
  }
  return DEFAULT_TABS();
}

const PublicProfile = () => {
  const [loading, setLoading] = useState(true);
  const [tabs, setTabs] = useState(DEFAULT_TABS);
  const [publicContactEmail, setPublicContactEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const doc = await getBusiness();
      if (doc) {
        setTabs(normalizeTabs(doc.publicProfileTabs));
        setPublicContactEmail(doc.publicContactEmail || "");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateTab = useCallback((tabId, patch) => {
    setTabs((prev) =>
      prev.map((t) => (t.tabId === tabId ? { ...t, ...patch } : t))
    );
  }, []);

  const addTab = () => {
    setTabs((prev) => {
      const nextOrder =
        prev.length === 0
          ? 0
          : Math.max(...prev.map((t) => t.sortOrder)) + 1;
      return [
        ...prev,
        {
          tabId: `tab-${Date.now()}`,
          label: "New tab",
          visible: true,
          sortOrder: nextOrder,
          contentKey: "custom",
          richTextHtml: "",
          galleryImageUrls: [],
          workingHoursHtml: "",
          contactIntroHtml: "",
        },
      ];
    });
  };

  const removeTab = (tabId) => {
    setTabs((prev) => prev.filter((t) => t.tabId !== tabId));
  };

  const onGalleryUpload = async (tabId, info) => {
    const { file } = info;
    if (!file) return;
    const isJpgOrPng =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";
    if (!isJpgOrPng) {
      message.error("JPG, PNG, or WebP only");
      return;
    }
    const formData = new FormData();
    formData.append("image", file);
    try {
      const response = await serviceImage(formData);
      const url = response?.url;
      if (url) {
        setTabs((prev) =>
          prev.map((t) =>
            t.tabId === tabId
              ? { ...t, galleryImageUrls: [...(t.galleryImageUrls || []), url] }
              : t
          )
        );
        message.success("Image added");
      }
    } catch (e) {
      message.error("Upload failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateBusiness({
        publicProfileTabs: tabs,
        publicContactEmail: publicContactEmail.trim(),
      });
      if (res) {
        message.success("Public profile saved");
        load();
      }
    } catch (e) {
      message.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const collapseItems = tabs.map((tab) => ({
    key: tab.tabId,
    label: (
      <span className="font-medium">
        {tab.label || "Untitled"}{" "}
        <span className="text-gray-400 font-normal text-xs">
          ({tab.contentKey})
        </span>
      </span>
    ),
    children: (
      <div className="flex flex-col gap-3 pt-1">
        <Form layout="vertical" className="max-w-xl">
          <Form.Item label="Tab label">
            <Input
              value={tab.label}
              onChange={(e) => updateTab(tab.tabId, { label: e.target.value })}
            />
          </Form.Item>
          <Space wrap>
            <div className="flex items-center gap-2">
              <span>Visible on YupUp</span>
              <Switch
                checked={tab.visible}
                onChange={(v) => updateTab(tab.tabId, { visible: v })}
              />
            </div>
            <Form.Item label="Sort order" className="mb-0">
              <InputNumber
                min={0}
                value={tab.sortOrder}
                onChange={(v) => updateTab(tab.tabId, { sortOrder: v ?? 0 })}
              />
            </Form.Item>
          </Space>
          <Form.Item
            label="Content key"
            extra="Common: deals, about, contact, photos, custom — or any label you choose."
          >
            <Input
              value={tab.contentKey}
              onChange={(e) =>
                updateTab(tab.tabId, { contentKey: e.target.value })
              }
            />
          </Form.Item>
        </Form>

        {(tab.contentKey === "about" ||
          tab.contentKey === "custom" ||
          tab.contentKey === "photos") && (
          <div>
            <p className="text-sm font-medium mb-1">Story / extra HTML</p>
            <ReactQuill
              theme="snow"
              value={tab.richTextHtml}
              onChange={(html) => updateTab(tab.tabId, { richTextHtml: html })}
              modules={quillModules}
            />
          </div>
        )}

        {tab.contentKey === "contact" && (
          <>
            <div>
              <p className="text-sm font-medium mb-1">Intro (HTML)</p>
              <ReactQuill
                theme="snow"
                value={tab.contactIntroHtml}
                onChange={(html) =>
                  updateTab(tab.tabId, { contactIntroHtml: html })
                }
                modules={quillModules}
              />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Working hours (HTML)</p>
              <ReactQuill
                theme="snow"
                value={tab.workingHoursHtml}
                onChange={(html) =>
                  updateTab(tab.tabId, { workingHoursHtml: html })
                }
                modules={quillModules}
              />
            </div>
          </>
        )}

        <div>
          <p className="text-sm font-medium mb-1">Gallery images</p>
          <Upload
            accept="image/png,image/jpeg,image/webp"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={(info) => onGalleryUpload(tab.tabId, info)}
          >
            <Button type="dashed">Upload photo</Button>
          </Upload>
          {tab.galleryImageUrls?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-4">
              {tab.galleryImageUrls.map((url, idx) => (
                <div
                  key={`${url}-${idx}`}
                  className="flex flex-col items-center gap-1"
                >
                  <div className="w-[104px] h-[104px] rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shrink-0">
                    <Image
                      src={url}
                      alt=""
                      width={104}
                      height={104}
                      style={{
                        width: 104,
                        height: 104,
                        objectFit: "cover",
                        display: "block",
                      }}
                      preview={{ mask: "Preview" }}
                    />
                  </div>
                  <Button
                    type="link"
                    size="small"
                    danger
                    className="p-0 h-auto leading-none"
                    onClick={() =>
                      updateTab(tab.tabId, {
                        galleryImageUrls: tab.galleryImageUrls.filter(
                          (_, i) => i !== idx
                        ),
                      })
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Popconfirm
          title="Remove this tab?"
          onConfirm={() => removeTab(tab.tabId)}
        >
          <Button danger size="small">
            Remove tab
          </Button>
        </Popconfirm>
      </div>
    ),
  }));

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <Card title="Public profile on YupUp" className="p-2">
        <p className="text-gray-600 text-sm mb-4">
          Choose which tabs customers see on your YupUp business page, rename
          them, add photos and rich text. Use content keys like{" "}
          <strong>deals</strong>, <strong>about</strong>, <strong>contact</strong>,{" "}
          <strong>photos</strong>, or any custom key for a simple HTML block.
        </p>
        <Form layout="vertical" className="max-w-xl mb-4">
          <Form.Item label="Public contact email (optional)">
            <Input
              type="email"
              value={publicContactEmail}
              onChange={(e) => setPublicContactEmail(e.target.value)}
              placeholder="hello@business.com"
            />
          </Form.Item>
        </Form>
        <Space className="mb-3">
          <Button onClick={addTab}>Add tab</Button>
        </Space>
        <Collapse items={collapseItems} />
        <Button
          type="primary"
          className="mt-4"
          style={{ backgroundColor: "#6B43E0" }}
          loading={saving}
          onClick={save}
        >
          Save
        </Button>
      </Card>
    </div>
  );
};

export default PublicProfile;
